/**
 * Panel-left top zone — the wayfinding strip at the top of the left rail.
 *
 * Phase 1a: tab strip with icons + count badges.
 * Phase 1b: filled the breadcrumb slot (mounted ScopeBreadcrumb).
 * Phase 2:  breadcrumb slot REMOVED. The scope back-affordance now lives
 *           inside the property panel's section-view header as a compact
 *           "‹ Home · Hero" back-chip — see property-panel.js
 *           _renderScopeHeader(). Rationale: the top-zone breadcrumb was
 *           always-on chrome that mostly showed "Sitewide › Home" (zero
 *           information at page-level), and its narrow column forced
 *           horizontal scroll when section names ran long. The chip exists
 *           only when scope is below page-level — same as Figma/Webflow.
 *
 * The per-panel search input previously rendered here was removed in favor
 * of the top-bar command palette (`⌘K` / `/`). The `panel:search` /
 * `panel:search-submit` event names are kept reserved so section-list and
 * other consumers don't need a coordinated removal — they simply never fire.
 *
 * Owns no application state — purely renders + emits events. The host
 * (NavSidebar) keeps the activeTab source-of-truth and calls setActiveTab().
 */

import { attachTablistNav } from './tablist-nav.js';
import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { disposeFindings } from '../shared/finding-disposition.js';

/** Pure seam: the dot is derived from OPEN findings, never raw totals. */
export function openSeverityForReport(report, auditAccepts = null) {
  const disposed = disposeFindings(report?.findings || [], auditAccepts);
  const counts = disposed.counts;
  return {
    count: (counts.blocking || 0) + (counts.warning || 0),
    severity: counts.blocking ? 'blocking' : counts.warning ? 'warning' : ''
  };
}

const ICONS = {
  // Two overlapping pieces — a page IS a set of distinct blocks, and the mark
  // says "parts" rather than "a list". It replaced three loose horizontal
  // bars, which is the same mark the section LIST rows already draw.
  //
  // NOT the box/tray alternative, deliberately: the Project tab sits directly
  // beside this one, and `projects` in the icon registry is already a folder
  // meaning "a project". Two adjacent tabs wearing container glyphs is how you
  // get a strip nobody can read at a glance.
  sections:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="11.5" y="3" width="9.5" height="9.5" rx="2.5"/><circle cx="8" cy="16" r="5"/></svg>',
  design:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><circle cx="7.5" cy="10.5" r="1" fill="currentColor"/><circle cx="12" cy="7.5" r="1" fill="currentColor"/><circle cx="16.5" cy="10.5" r="1" fill="currentColor"/><path d="M12 12c0 4 4 4 4 6a3 3 0 0 1-3 3"/></svg>',
  'client-seo':
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>',
  audit:
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 11l2 2 4-5"/><path d="M20 12c0 5-3.5 8-8 10-4.5-2-8-5-8-10V5l8-3 8 3z"/></svg>'
};

export class PanelTopZone {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.container — element to mount inside
   * @param {Array<{id, label, internalId}>} opts.tabs
   * @param {string} opts.activeTabId
   * @param {(id: string) => void} opts.onTabClick
   */
  constructor({ container, tabs, activeTabId, onTabClick }) {
    this._container = container;
    this._tabs = tabs;
    this._activeTabId = activeTabId;
    this._onTabClick = onTabClick;
    this._listeners = [];
    this._render();
    this._wireEvents();
    this._refreshBadges();
  }

  _render() {
    const root = document.createElement('div');
    root.className = 'pb-ptz';

    // Row 1 — tab strip (search input removed; ⌘K / `/` open the command
    // palette in the top bar instead).
    const nav = document.createElement('div');
    nav.className = 'pb-ptz-tabs';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Sidebar panels');

    for (const tab of this._tabs) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pb-ptz-tab';
      btn.setAttribute('role', 'tab');
      btn.id = `pb-sidebar-tab-${tab.internalId}`;
      btn.setAttribute('aria-selected', tab.internalId === this._activeTabId ? 'true' : 'false');
      btn.setAttribute('aria-controls', `pb-sidebar-pane-${tab.internalId}`);
      btn.dataset.tab = tab.internalId;

      const icon = document.createElement('span');
      icon.className = 'pb-ptz-tab-icon';
      icon.innerHTML = ICONS[tab.internalId] || '';
      btn.appendChild(icon);

      const label = document.createElement('span');
      label.className = 'pb-ptz-tab-label';
      // Chromium: the full audit label is 101px in a 73px slot at the
      // default 452px rail. Keep the approved short label with its full name.
      label.textContent = tab.internalId === 'audit' ? 'Checks' : tab.label;
      if (tab.internalId === 'audit') {
        btn.setAttribute('aria-label', tab.label);
        btn.dataset.tooltip =
          'Publish checks: review anything that needs attention before publishing.';
      }
      btn.appendChild(label);

      const badge = document.createElement('span');
      badge.className = 'pb-ptz-tab-badge';
      badge.dataset.role = 'badge';
      badge.hidden = true;
      btn.appendChild(badge);

      btn.addEventListener('click', () => this._onTabClick(tab.internalId));
      nav.appendChild(btn);
    }
    root.appendChild(nav);

    // Row 2 (breadcrumb slot) removed in Phase 2 — scope back-chip lives
    // inside the property-panel section-view header instead.

    this._container.appendChild(root);
    this._navEl = nav;
    this._tabNav = attachTablistNav(nav, {
      onActivate: (tab) => this._onTabClick(tab.dataset.tab)
    });
  }

  _wireEvents() {
    // Re-count whenever state changes — sections may be added/removed and
    // overrides may be set/cleared elsewhere in the app.
    this._listen('state-changed', () => this._refreshBadges());
    this._listen('pre-publish-audit:report', (report = {}) => {
      // P6: a DOT, not a number. A count invites arithmetic ("is 7 worse than
      // 5?"); a dot invites a glance. Its colour is the worst OPEN severity —
      // accepted findings are decisions, not outstanding work, so they must not
      // keep the indicator lit.
      this._auditReport = report;
      this._refreshBadges();
    });
  }

  _listen(event, fn) {
    eventBus.on(event, fn);
    this._listeners.push([event, fn]);
  }

  _refreshBadges() {
    if (!this._navEl) return;
    if (this._auditReport) {
      let accepts = null;
      try {
        accepts = store.getState()?.auditAccepts || null;
      } catch {
        /* state may not be initialized in an embedding/test context */
      }
      const open = openSeverityForReport(this._auditReport, accepts);
      this._auditBadgeCount = open.count;
      this._auditSeverity = open.severity;
    }
    const counts = this._computeCounts();
    for (const btn of this._navEl.querySelectorAll('.pb-ptz-tab')) {
      const badge = btn.querySelector('[data-role="badge"]');
      if (!badge) continue;
      const id = btn.dataset.tab;
      const n = counts[id] ?? 0;
      if (id === 'audit') {
        // The Publish checks indicator is a dot; every other tab keeps its count.
        const sev = this._auditSeverity || '';
        badge.textContent = '';
        badge.className = `pb-ptz-dot${sev ? ` pb-ptz-dot--${sev}` : ''}`;
        badge.hidden = !sev;
        badge.setAttribute(
          'aria-label',
          sev === 'blocking' ? 'Publish checks need attention' : 'Some things are worth a look'
        );
        const tip =
          sev === 'blocking'
            ? 'Publish checks need attention'
            : sev === 'warning'
              ? 'Some things are worth a look'
              : '';
        if (tip) badge.dataset.tooltip = tip;
        else delete badge.dataset.tooltip;
        continue;
      }
      if (n > 0) {
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    }
  }

  _computeCounts() {
    const counts = { sections: 0, design: 0, 'client-seo': 0, audit: this._auditBadgeCount || 0 };
    let state;
    try {
      state = store.getState();
    } catch {
      return counts;
    }
    if (!state) return counts;

    // Sections: count sections on the active page (shared nav/footer excluded —
    // they live separately and the badge represents the editable list).
    let page = null;
    try {
      page = store.getActivePage();
    } catch {
      /* no active page yet */
    }
    if (page && Array.isArray(page.sections)) {
      counts.sections = page.sections.length;
    }

    // Design: count sections (active page + shared) that carry per-element overrides.
    let designCount = 0;
    const hasOverrides = (sec) => {
      const els = sec?.overrides?.elements;
      return !!(els && typeof els === 'object' && Object.keys(els).length > 0);
    };
    if (page && Array.isArray(page.sections)) {
      for (const sec of page.sections) if (hasOverrides(sec)) designCount++;
    }
    if (state.sharedSections?.navigation && hasOverrides(state.sharedSections.navigation))
      designCount++;
    if (state.sharedSections?.footer && hasOverrides(state.sharedSections.footer)) designCount++;
    counts.design = designCount;

    return counts;
  }

  // ── Public API ──

  setActiveTab(id) {
    this._activeTabId = id;
    if (this._navEl) {
      for (const btn of this._navEl.querySelectorAll('[role="tab"]')) {
        btn.setAttribute('aria-selected', btn.dataset.tab === id ? 'true' : 'false');
      }
      this._tabNav?.sync();
    }
  }

  destroy() {
    this._tabNav?.destroy();
    for (const [event, fn] of this._listeners) eventBus.off(event, fn);
    this._listeners = [];
  }
}
